import { RefObject, useCallback } from "react";
import useSpaces from "hooks/useSpaces";
import useBarriers from "hooks/useBarriers";
import useHealthcareMarkers from "hooks/useHealthcareMarkers";
import useFoodMarkers from "hooks/useFoodMarkers";
import useEducationMarkers from "hooks/useEducationMarkers";
import useHousingMarkers from "hooks/useHousingMarkers";
import useTransportationMarkers from "hooks/useTransportationMarkers";
import useCommunityMarkers from "hooks/useCommunityMarkers";
import useEconomicMarkers from "hooks/useEconomicMarkers";

type Arguments = {
	map: RefObject<any | null>;
};

const useMapEvents = ({ map }: Arguments) => {
	const { showSpace, toggleSpaceActive } = useSpaces();

	const { barrierMarkers, setBarrierMarkers, handleBarrierMarkers } =
		useBarriers(map);

	const { healthcareMarkers, setHealthcareMarkers, handleHealthcareMarkers } =
		useHealthcareMarkers(map);

	const { foodMarkers, setFoodMarkers, handleFoodMarkers } =
		useFoodMarkers(map);

	const { educationMarkers, setEducationMarkers, handleEducationMarkers } =
		useEducationMarkers(map);

	const { communityMarkers, setCommunityMarkers, handleCommunityMarkers } =
		useCommunityMarkers(map);

	const { housingMarkers, setHousingMarkers, handleHousingMarkers } =
		useHousingMarkers(map);

	const { economicMarkers, setEconomicMarkers, handleEconomicMarkers } =
		useEconomicMarkers(map);

	const {
		transportationMarkers,
		setTransportationMarkers,
		handleTransportationMarkers,
	} = useTransportationMarkers(map);

	// Add function to remove all markers of a specific type
	const removeMarkers = useCallback(
		(
			type: string,
			category:
				| "healthcare"
				| "food"
				| "education"
				| "community"
				| "transportation"
				| "housing"
				| "economic"
				| "barriers"
		) => {
			const markerStates = {
				healthcare: {
					markers: healthcareMarkers,
					setMarkers: setHealthcareMarkers,
				},
				food: { markers: foodMarkers, setMarkers: setFoodMarkers },
				education: {
					markers: educationMarkers,
					setMarkers: setEducationMarkers,
				},
				community: {
					markers: communityMarkers,
					setMarkers: setCommunityMarkers,
				},
				transportation: {
					markers: transportationMarkers,
					setMarkers: setTransportationMarkers,
				},
				housing: { markers: housingMarkers, setMarkers: setHousingMarkers },
				economic: {
					markers: economicMarkers,
					setMarkers: setEconomicMarkers,
				},
				barriers: {
					markers: barrierMarkers,
					setMarkers: setBarrierMarkers,
				},
			};

			const { markers, setMarkers } = markerStates[category];

			// Remove all markers of this type from the map
			markers[type]?.forEach((marker: any) => marker.remove());

			// Clear the markers array for this type
			setMarkers((prev: any) => ({
				...prev,
				[type]: [],
			}));
		},
		[
			healthcareMarkers,
			foodMarkers,
			educationMarkers,
			communityMarkers,
			transportationMarkers,
			housingMarkers,
			economicMarkers,
			barrierMarkers,
			setHealthcareMarkers,
			setFoodMarkers,
			setEducationMarkers,
			setCommunityMarkers,
			setTransportationMarkers,
			setHousingMarkers,
			setEconomicMarkers,
			setBarrierMarkers,
		]
	);

	return {
		showSpace,
		toggleSpaceActive,
		removeMarkers,
		handleBarrierMarkers,
		handleHealthcareMarkers,
		handleFoodMarkers,
		handleEducationMarkers,
		handleCommunityMarkers,
		handleTransportationMarkers,
		handleHousingMarkers,
		handleEconomicMarkers,
	};
};

export default useMapEvents;
